import { useState, useEffect, useCallback, lazy } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { storeService } from './services/store.service';
import { Notification } from '@arco-design/web-react';
import './App.css';
import SEO from './components/SEO';
import AnnouncementBanner from './components/AnnouncementBanner';

// Lazy helper for Suspense + ErrorBoundary
import { Lazy } from './components/Lazy/Lazy';

// EAGER IMPORTS - Critical path components (always needed on first load)
import MainPage from './components/MainPage/MainPage';
import MenuOverlay from './components/MenuOverlay/MenuOverlay';
import { LocationOverlay } from './components/LocationOverlay/LocationOverlay';
import LoginGate from './components/LoginGate/LoginGate';
import GoogleOneTap from './components/GoogleOneTap/GoogleOneTap';

// Type imports and utilities
import { Store } from './types/location.types';
import { optimizedApiService, CMS_BASE_URL } from './config/api.config';
import { slugifyStoreName, findStoreBySlug, getBookingUrl } from './utils/slug';
import { petsStore } from './services/pets.store';
import { signOut } from './services/signOut';
import { SESSION_ENDED_EVENT } from './services/sessionEvents';

// Wrapper components (extracted for cleaner App.tsx)
import BookingFlowWrapper from './components/BookingFlow/BookingFlowWrapper';
import AppointmentsWrapper from './components/Appointments/AppointmentsWrapper';
import MyPackagesWrapper from './components/MyPackages/MyPackagesWrapper';
import PerksWrapper from './components/Perks/PerksWrapper';
import PromotionsWrapper from './components/Promotions/PromotionsWrapper';
import PromotionPopup, { SIGNED_IN_EVENT } from './components/Promotions/PromotionPopup';
import WaitlistWrapper from './components/Waitlist/WaitlistWrapper';

// Demo mode
import { applyLinkStore } from './services/campaignStore';
import { isSelectLocationPath } from './utils/locationChoice';
import { isDemoStore, isDemoModeActive, activateDemoMode, deactivateDemoMode, DEMO_STORE_SLUG } from './utils/demoMode';
import { DemoModeProvider } from './context/DemoModeContext';

// LAZY IMPORTS - Route components (loaded on demand)
// Pets: the pet steps (SF 65–68) and the one pet sheet (SF 34), phase 4
const FirstPetFlow = lazy(() => import('./components/BookingNext/PetSheet/FirstPetFlow'));
const PetSheet = lazy(() => import('./components/BookingNext/PetSheet/PetSheet'));
const PetProfileSuccess = lazy(() => import('./components/PetProfileSuccess/PetProfileSuccess'));
const SelectPets = lazy(() => import('./components/SelectPets/SelectPets'));

// Booking flow
const ReschedulePage = lazy(() => import('./components/BookingNext/Reschedule/ReschedulePage'));
// My booking (SF 08–10): one of the client's bookings, opened from My bookings.
const MyBookingPage = lazy(() => import('./components/BookingNext/MyBooking/MyBookingPage'));
// The booked page (SF 75/76): every way in after Review and book.
const BookedPage = lazy(() => import('./components/BookingNext/Booked/BookedPage'));

// Payment
const PaymentTransitionWrapper = lazy(() => import('./components/PaymentTransition/PaymentTransitionWrapper'));
const StripeRedirect = lazy(() => import('./components/StripeRedirect/StripeRedirect'));

// Error pages
const NotFound = lazy(() => import('./components/NotFound/NotFound'));

// SEO
const SitemapRoute = lazy(() => import('./components/SitemapRoute/SitemapRoute'));

// Auth & Profile
const EditUserProfile = lazy(() => import('./components/EditUserProfile/EditUserProfile'));
const PetProfiles = lazy(() => import('./components/PetProfiles/PetProfiles'));
// My account › Addresses (mobile grooming, SF 109).
const AddressesPage = lazy(() => import('./components/Addresses/AddressesPage'));
// A salon's first-to-tap offer (mpgadmin G156): the link in the text or email.
const GapOfferPage = lazy(() => import('./components/GapOffer/GapOfferPage'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail/VerifyEmail'));
const ResetPassword = lazy(() => import('./components/ResetPassword/ResetPassword'));

// Add Step type definition
type Step =
  | 'main'
  | 'name'
  | 'breed'
  | 'weight'
  | 'success'
  | 'booking'
  | 'service'
  | 'pet-profile-success'
  | 'edit-pet-profile'
  | 'create-pet-profile'
  | 'select-pets';

interface Pet {
  id?: number;
  type?: 'dog' | 'cat';
  name?: string;
  breed?: string;
  breedId?: number;
  gender?: string;
  weight?: string;
  age?: number;
  dob?: string;
  aggression?: string;
  info?: string;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<Step>('main');

  /*
   * The step-based pages (create-a-pet, select pets, the old service page)
   * render here, above the router's own pages, whatever the URL is. Handing
   * off to the routed booking flow (/book/…) navigates without resetting
   * the step, so a step page could stay mounted on top of the routed one —
   * two "Book a Groom" pages, the top one holding App's own pet selection,
   * closing it revealing the other. On a routed page the step is always
   * the home page.
   */
  useEffect(() => {
    const routed = /^\/(book|appointments|profile|my-perks|my-waitlist|promotions|pet|reschedule|payment|storefront|auth|verify-email|reset-password|spc)(\/|$)/.test(location.pathname);
    if (routed && currentStep !== 'main') setCurrentStep('main');
  }, [location.pathname, currentStep]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Initialize auth state synchronously from localStorage to prevent flickering
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const jwt = localStorage.getItem('cms_jwt');
    return !!jwt;
  });
  const [selectedStore, setSelectedStore] = useState<Store | null>(() => {
    const stored = localStorage.getItem('selectedStore');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [storeCount, setStoreCount] = useState<number>(() => {
    const stored = localStorage.getItem('storeCount');
    return stored ? parseInt(stored, 10) : 0;
  });
  const [user, setUser] = useState<any | null>(null); // Changed to any as UserProfile is removed
  const [pets, setPets] = useState<Pet[]>([]);
  const [editingPetIndex, setEditingPetIndex] = useState<number | null>(null);
  const [selectedPets, setSelectedPets] = useState<Pet[]>([]);
  const [locationOverlayOpen, setLocationOverlayOpen] = useState(false);
  const [showLoginGate, setShowLoginGate] = useState(false);

  // Let deep / routed components (e.g. the booking summary's "number already registered" block)
  // open the sign-in gate without prop-threading through lazy route wrappers.
  useEffect(() => {
    const openLogin = () => setShowLoginGate(true);
    window.addEventListener('mpg:open-login', openLogin);
    return () => window.removeEventListener('mpg:open-login', openLogin);
  }, []);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(() => {
    // Initialize from cache for instant display
    const cached = localStorage.getItem('company_logo_cache');
    return cached || null;
  });
  const [shouldShowExistingPets, setShouldShowExistingPets] = useState(false);
  const [isLoadingPets, setIsLoadingPets] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);

  // Client ID is now stored in localStorage via auth service (Step 1)
  // Removed clientId state - using setCMSClientId/getCMSClientId instead
  
  // Stable callbacks for PetProfileSuccess
  const handlePetsLoaded = useCallback((loadedPets: Pet[]) => {
    if (!isLoadingPets) {
      setPets(loadedPets);
      setShouldShowExistingPets(true);
    }
  }, [isLoadingPets]);

  /*
   * Every sign-in settles the account's pets here, whichever door was used
   * (a reload after the login gate, Google One Tap, the in-page handler).
   * Two things used to depend on the door: a pet made before signing in was
   * only copied into the account when the Google redirect or the service
   * page had armed the flag, so a home-page pet + a mobile-number sign-in
   * never copied it; and a sign-in without a reload left `pets` holding the
   * pre-login draft list, so the picker showed just that one pet while the
   * chips showed the account's.
   *
   * Order matters: copy first (the migration service dedups by name and
   * type, consumes its flag before any async work, and shares one run with
   * the service page's and profile page's callers, so nothing is created
   * twice), then load the account's pets, which now include the copies.
   */
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      setIsLoadingPets(true);
      try {
        // The store settles the pets (copy, then fetch); App mirrors them.
        // A list read moments ago for this sign-in stands (back from the booking pages).
        const account = await petsStore.settleRecent();
        if (cancelled) return;
        const { ensureCMSUser } = await import('./services/auth.service');
        const apiUser = await ensureCMSUser();
        if (cancelled) return;
        if (apiUser) setUser(apiUser);
        setPets(account);
        setShouldShowExistingPets(true);
      } catch (err) {
        console.error('[Auth] Settling pets after sign-in failed:', err);
      } finally {
        if (!cancelled) {
          setIsLoadingPets(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);
  
  // Add useEffect to check localStorage on mount and load CMS pets
  // OPTIMIZED: Uses Promise.allSettled for parallel data fetching
  useEffect(() => {
    // Cleanup flag to prevent setState on unmounted component
    let cancelled = false;

    const initializeAuth = async () => {
      // Manual timer (avoids StrictMode double-invoke console.time collision)
      const initStart = performance.now();

      // Import services (static imports would be better but keeping dynamic for compatibility)
      const { storageService } = await import('./services/storage.service');
      const { getCMSJWT, clearCMSAuth, setPetsCache, hasPublicPets, getPublicPets } = await import('./services/auth.service');
      const { fetchCurrentUserAndPets, fetchStores } = await import('./services/api');

      // STEP 1: Handle JWT from URL first (must be sequential - affects subsequent auth checks)
      const jwtParams = new URLSearchParams(window.location.search);
      const urlJWT = jwtParams.get('jwt');
      if (urlJWT) {
        try {
          localStorage.setItem('cms_jwt', urlJWT);
        } catch (e) {
          console.error('[Auth] Failed to store jwt to localStorage:', e);
        }
        // Only Google's sign-in comes back with ?jwt= (the CMS's callback).
        const { rememberSignInMethod } = await import('./services/lastSignInMethod');
        rememberSignInMethod('google');
        try {
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete('jwt');
          const newUrl = currentUrl.pathname + (currentUrl.search || '') + (currentUrl.hash || '');
          window.history.replaceState({}, document.title, newUrl);
        } catch (e) {
          console.error('[Auth] Failed to clean jwt from URL:', e);
        }
      }

      // STEP 2: Handle URL step parameters (for redirects and reschedule)
      const urlParams = new URLSearchParams(window.location.search);
      const stepParam = urlParams.get('step');
      const fromPublic = urlParams.get('from_public');

      if (stepParam) {
        const hasJWT = localStorage.getItem('cms_jwt');
        const isUserAuthenticated = isAuthenticated || !!hasJWT;

        // Redirect authenticated users from old booking URLs to new flow
        if (isUserAuthenticated && ['pet-profile-success', 'booking', 'dog-grooming', 'cat-grooming', 'service'].includes(stepParam)) {
          const stepMapping: { [key: string]: string } = {
            'pet-profile-success': '/book/services',
            'booking': '/book/services',
            'dog-grooming': '/book/services?category=dog',
            'cat-grooming': '/book/services?category=cat',
            'service': '/book/services'
          };
          if (fromPublic === 'true') {
            storageService.setItem('create_from_public_pets', 'true');
          }
          navigate(stepMapping[stepParam] || '/book', { replace: true });
          console.log(`🔄 Total Init Time: ${(performance.now() - initStart).toFixed(3)} ms`);
          return;
        }

        // Set current step for non-authenticated users
        if (!cancelled) setCurrentStep(stepParam as Step);
        if (fromPublic === 'true') {
          storageService.setItem('create_from_public_pets', 'true');
        }

        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // STEP 3: Set loading state
      if (cancelled) return;
      setIsLoadingPets(true);

      // STEP 4: Fetch data in PARALLEL using Promise.allSettled
      const jwt = getCMSJWT();
      const storedStoreStr = storageService.getItem('selectedStore');
      const storedStoreCountStr = storageService.getItem('storeCount');

      // Restore cached values immediately for faster UI
      if (storedStoreCountStr && !cancelled) {
        setStoreCount(parseInt(storedStoreCountStr, 10));
      }
      // The location this phone chose: its menus and breeds start now, beside the reads below,
      // instead of after the client's record has answered (the services step finds them cached).
      let preloadedStoreId: number | null = null;
      if (storedStoreStr && !cancelled) {
        try {
          const stored = JSON.parse(storedStoreStr);
          setSelectedStore(stored);
          if (stored?.id) {
            preloadedStoreId = stored.id;
            optimizedApiService.preloadStoreData(stored.id).catch(err => {
              console.error('[Auth] Preload store data failed:', err);
            });
          }
        } catch (e) { /* ignore parse error */ }
      }

      const parallelFetchesStart = performance.now();
      const results = await Promise.allSettled([
        // Fetch 1: User & Pets (if authenticated)
        jwt
          ? fetchCurrentUserAndPets().catch(err => {
              console.error('[Auth] User/pets fetch failed:', err);
              throw err;
            })
          : Promise.resolve(null),

        // Fetch 2: Stores list (always fetch to get latest)
        fetchStores().catch(err => {
          console.error('[Auth] Stores fetch failed:', err);
          return []; // Fallback to empty array
        })
      ]);
      console.log(`  ├─ Parallel Fetches: ${(performance.now() - parallelFetchesStart).toFixed(3)} ms`);

      if (cancelled) return;

      // STEP 5: Process results
      const [userResult, storesResult] = results;

      // Process User + Pets result
      if (userResult.status === 'fulfilled' && userResult.value) {
        const { user: apiUser, pets: apiPets } = userResult.value;

        setUser(apiUser);
        setIsAuthenticated(true);
        storageService.setItem('user', JSON.stringify(apiUser));
        storageService.setItem('cms_user', JSON.stringify(apiUser));
        if (apiUser?.id) {
          storageService.setItem('cms_client_id', apiUser.id.toString());
        }
        setPets(apiPets || []);
        setShouldShowExistingPets(true);
        setPetsCache(apiPets || []);

      } else if (userResult.status === 'rejected' && jwt) {
        if ((userResult.reason as Error)?.message === 'SESSION_EXPIRED') {
          // The CMS said 401 (G10): the token is expired or invalid, is
          // another salon's, or names a client the salon no longer has
          // (merged, removed; CMS G11, G13). fetchCurrentUserAndPets has
          // already signed out (G1); a client that is gone is told so by
          // the SESSION_ENDED_EVENT listener.
          console.warn('[Auth] Session ended:', userResult.reason);
          clearCMSAuth();
          setUser(null);
          setIsAuthenticated(false);
          setPets([]);
          storageService.removeItem('user');
        } else {
          // Anything else is the server or the network having a moment (a
          // 5xx, a timeout, a read cut off by the reload that follows a
          // sign-in): keep the person signed in rather than wiping their
          // session over a blip (G10). Before, every failure here signed
          // them out silently, a brand-new session included.
          console.error('[Auth] Could not refresh the signed-in client:', userResult.reason);
        }
      } else if (!jwt) {
        // No JWT - load public pets if any
        const publicPets = getPublicPets();
        if (publicPets.length > 0 && !cancelled) {
          setPets(publicPets);
        }
      }

      // Process Stores result
      let linkedStore: Store | null = null;
      if (storesResult.status === 'fulfilled') {
        const stores = storesResult.value || [];
        // A campaign link's location (?store=, D27, G44): one of this tenant's
        // stores wins over the one chosen last time; anything else is ignored.
        // Before the store count, so the home page never opens the location
        // overlay over it.
        linkedStore = applyLinkStore(stores);
        if (linkedStore && !cancelled) {
          setSelectedStore(linkedStore);
          if (linkedStore.id !== preloadedStoreId) {
            preloadedStoreId = linkedStore.id;
            optimizedApiService.preloadStoreData(linkedStore.id).catch(err => {
              console.error('[Auth] Preload store data failed:', err);
            });
          }
        }
        setStoreCount(stores.length);
        storageService.setItem('storeCount', stores.length.toString());

        // Get company data from cache (populated by storeService)
        const { storeService } = await import('./services/store.service');
        const logoUrl = storeService.getCompanyLogoUrl();
        const companyName = storeService.getCompanyName();

        if (logoUrl) {
          setCompanyLogoUrl(logoUrl);

          // Update favicon dynamically with proper scaling
          const updateFavicon = (url: string, size: number) => {
            return new Promise<string>((resolve) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  // Calculate scaling to fit image in square while maintaining aspect ratio
                  const scale = Math.min(size / img.width, size / img.height);
                  const width = img.width * scale;
                  const height = img.height * scale;
                  const x = (size - width) / 2;
                  const y = (size - height) / 2;

                  ctx.clearRect(0, 0, size, size);
                  ctx.drawImage(img, x, y, width, height);
                  resolve(canvas.toDataURL('image/png'));
                } else {
                  resolve(url); // Fallback to original URL
                }
              };
              img.onerror = () => resolve(url); // Fallback to original URL on error
              img.src = url;
            });
          };

          // Create properly sized favicons
          updateFavicon(logoUrl, 32).then((faviconDataUrl) => {
            const faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement;
            if (faviconLink) faviconLink.href = faviconDataUrl;
          });

          updateFavicon(logoUrl, 180).then((appleTouchDataUrl) => {
            const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement;
            if (appleTouchIcon) appleTouchIcon.href = appleTouchDataUrl;
          });
        }

        if (companyName) {
          document.title = `${companyName} - Book now`;
        }

        // Check if URL contains a store slug (e.g., /furtastic-melbourne)
        const pathname = window.location.pathname;
        const potentialSlug = pathname.split('/')[1]; // Get first path segment

        // First path segments that belong to the app's own routes, so they are
        // never mistaken for a store slug.
        //
        // Compared with `===`, NOT `startsWith`. The prefix test silently ate
        // every store whose slug merely BEGAN with one of these words — on a
        // pet grooming platform, "Pet Palace" and "Petite Paws" both match
        // 'pet', "Bookings Barn" matches 'book' — and the failure is invisible:
        // the store is simply never selected, with no error, so a shareable
        // link opens the home page with the wrong branch (or none) chosen.
        // Every first segment the router actually declares is a whole word, so
        // exact matching costs nothing.
        //
        // `reset-password`, `verify-email`, `storefront` and `sitemap.xml` are
        // router paths that were missing from this list, which meant they fell
        // through to the store lookup and set the 404 state on a multi-store
        // tenant. Added here rather than left for a separate fix: the list's
        // whole job is to name the app's own routes, and one that omits four of
        // them isn't doing it.
        const knownRoutes = [
          'appointments', 'book', 'profile', 'pet', 'reschedule', 'auth', 'payment', 'spc',
          'reset-password', 'verify-email', 'storefront', 'sitemap.xml',
          // MPG Perks wallet (the public /perks catalogue is served by the
          // SSR Worker and never reaches this app).
          'my-perks',
          // The customer's waitlist requests.
          'my-waitlist',
          // Salon promotions.
          'promotions',
          // The salon's "choose your location" link (D28).
          'select-location',
        ];
        const isKnownRoute = !!potentialSlug && knownRoutes.includes(potentialSlug);

        if (potentialSlug && !isKnownRoute && stores.length > 1) {
          // Try to find a store matching the slug
          const matchedStore = findStoreBySlug(stores, potentialSlug);
          if (matchedStore && !cancelled) {
            setSelectedStore(matchedStore);
            storageService.setItem('selectedStore', JSON.stringify(matchedStore));
            // Activate demo mode session if entering via demo store URL
            if (isDemoStore(matchedStore)) {
              activateDemoMode();
            }
          } else if (!matchedStore && !cancelled) {
            // Unknown path - show 404
            setShowNotFound(true);
          }
        } else if (potentialSlug && !isKnownRoute && stores.length <= 1) {
          // Single store but unknown path - show 404
          const matchedStore = findStoreBySlug(stores, potentialSlug);
          if (matchedStore && isDemoStore(matchedStore)) {
            activateDemoMode();
          }
          if (!matchedStore && !cancelled) {
            setShowNotFound(true);
          }
        }

        // Activate demo mode only when entering via /test-store URL
        if (potentialSlug === DEMO_STORE_SLUG) {
          activateDemoMode();
        } else {
          // Clear demo mode if navigating to a different store
          deactivateDemoMode();
        }

        // Auto-select if single store and no stored store
        if (stores.length === 1 && !storedStoreStr && !linkedStore) {
          const store = stores[0];
          setSelectedStore(store);
          storageService.setItem('selectedStore', JSON.stringify(store));
        }

      }

      // STEP 6: Handle post-login redirects
      if (jwt && userResult.status === 'fulfilled') {
        const preLoginStep = storageService.getItem('pre_login_step');
        const hasStoredPublicPets = hasPublicPets();

        if (preLoginStep) {
          let redirectUrl = '/';
          if (hasStoredPublicPets && preLoginStep === 'service') {
            redirectUrl = '/?step=pet-profile-success&from_public=true';
          } else if (preLoginStep === 'service') {
            redirectUrl = '/?step=pet-profile-success';
          } else if (preLoginStep === 'appointments') {
            redirectUrl = '/appointments';
          } else {
            redirectUrl = `/?step=${preLoginStep}`;
          }
          storageService.removeItem('pre_login_step');

          if (redirectUrl !== '/') {
            window.location.href = redirectUrl;
            console.log(`🔄 Total Init Time: ${(performance.now() - initStart).toFixed(3)} ms`);
            return;
          }
        }
      }

      // STEP 7: Preload store data in background (non-blocking)
      const finalStore = linkedStore ?? (storedStoreStr ? JSON.parse(storedStoreStr) : null) ??
        (storesResult.status === 'fulfilled' && storesResult.value?.length === 1 ? storesResult.value[0] : null);

      if (finalStore?.id && finalStore.id !== preloadedStoreId) {
        optimizedApiService.preloadStoreData(finalStore.id).catch(err => {
          console.error('[Auth] Preload store data failed:', err);
        });
      }

      console.log(`🔄 Total Init Time: ${(performance.now() - initStart).toFixed(3)} ms`);
    };

    // Execute initialization
    initializeAuth().finally(() => {
      if (!cancelled) {
        setIsLoadingPets(false);
      }
    });

    // Cleanup function
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]); // Removed isAuthenticated to prevent duplicate API calls on mobile

  // The old one-question-a-page pet steps (?step=name, breed, weight,
  // success, create-pet-profile) are the pet steps now (phase 4).
  useEffect(() => {
    if (['name', 'breed', 'weight', 'success', 'create-pet-profile'].includes(currentStep)) {
      setCurrentStep('main');
      navigate('/pet/create', { replace: true });
    }
  }, [currentStep, navigate]);

  const handleMainContinue = (type: 'dog' | 'cat') => {
    // The pet steps, with the type picked on the home page.
    navigate('/pet/create', { state: { petType: type } });
  };

  const handleStartBooking = async () => {
    // Check if store is selected first - location is required for booking
    if (!selectedStore) {
      // If there's only 1 store (or fetching), auto-select it instead of showing overlay
      if (storeCount <= 1) {
        try {
          const { fetchStores } = await import('./services/api');
          const { storageService } = await import('./services/storage.service');
          const stores = await fetchStores();
          
          if (stores.length === 1) {
            // Auto-select the single store
            const store = stores[0];
            const formattedStore = {
              ...store,
              id: typeof store.id === 'string' ? parseInt(store.id, 10) : store.id
            };
            setSelectedStore(formattedStore);
            storageService.setItem('selectedStore', JSON.stringify(formattedStore));
            setStoreCount(1);
            
            // Continue with booking after auto-selecting
            navigate(getBookingUrl('services', formattedStore));
            return;
          } else if (stores.length > 1) {
            // Multiple stores exist, show overlay
            setStoreCount(stores.length);
            setLocationOverlayOpen(true);
            return;
          }
        } catch (error) {
          console.error('Error fetching stores:', error);
        }
      }
      
      // Show location selection overlay (required for booking when multiple stores)
      setLocationOverlayOpen(true);
      return;
    }
    
    // Both authenticated and non-authenticated users use /book/services route
    // The services step asks a signed-out client to sign in when they add something.
    navigate(getBookingUrl('services', selectedStore));
  };

  // A stored session whose client is gone (merged into another record, or
  // removed; CMS G11, G13): signOut() has run, so mirror it here and say so
  // (G10). An expired token ends quietly, without this.
  useEffect(() => {
    const onSessionEnded = () => {
      setUser(null);
      setIsAuthenticated(false);
      setPets([]);
      setSelectedPets([]);
      Notification.info({
        id: 'session-ended',
        title: 'Please sign in again',
        content: 'Your account details were updated. Sign in to carry on.',
      });
    };
    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded);
  }, []);

  const handleLogout = () => {
    // One sign-out for every page: token, user, pet cache, pet store,
    // saved booking selection, pre-login markers (services/signOut.ts).
    signOut();
    setUser(null);
    setIsAuthenticated(false);
    setPets([]);
    setSelectedPets([]);
  };

  const handleLogin = () => {
    // Set authentication status
    setIsAuthenticated(true);
    // A promotion claim may be waiting on this sign-in.
    window.dispatchEvent(new Event(SIGNED_IN_EVENT));
    
    // Navigate to pet profile page to create or edit pets
    setCurrentStep('pet-profile-success');
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'name':
        setCurrentStep('main');
        break;
      case 'breed':
        setCurrentStep('name');
        break;
      case 'weight':
        setCurrentStep('breed');
        break;
      case 'success':
        setCurrentStep('weight');
        break;
      case 'select-pets':
        setCurrentStep('pet-profile-success');
        break;
      default:
        break;
    }
  };

  const handleLocationSelect = async (store: Store) => {
    const { storageService } = await import('./services/storage.service');

    // Ensure the id is a number when setting it
    const formattedStore = {
      ...store,
      id: typeof store.id === 'string' ? parseInt(store.id, 10) : store.id
    };
    setSelectedStore(formattedStore);

    // Store in storage for persistence
    storageService.setItem('selectedStore', JSON.stringify(formattedStore));

    // Update URL with store slug (only on main page, for shareable links)
    // Not on the salon's /select-location link, which stays as it is (Lena,
    // 29 Oct 2026: a refresh asks again; D28).
    if (storeCount > 1 && currentStep === 'main' && !isSelectLocationPath(window.location.pathname)) {
      const storeSlug = slugifyStoreName(formattedStore.name);
      const newUrl = `/${storeSlug}${window.location.search}`;
      window.history.replaceState({}, '', newUrl);
    }

    // Preload data for better performance when navigating to service pages
    if (formattedStore?.id) {
      optimizedApiService.preloadStoreData(formattedStore.id)
        .catch(error => {
          console.error('❌ Failed to preload store data:', error);
        });
    }
  };

  // Select pets hands over to the services step: the old services page
  // retired with phase 3c (G17).
  const handlePetSelection = (selected: Pet[]) => {
    setSelectedPets(selected);
    setCurrentStep('service');
  };

  const handlePetDeletion = async (index: number) => {
    const petToDelete = pets[index];
    console.log('🗑️ Attempting to delete pet:', petToDelete);
    
    try {
      // Check if user is authenticated
      if (isAuthenticated) {
        // Authenticated user - delete from CMS
        const { getCMSJWT, updatePetsCache } = await import('./services/auth.service');
        const jwt = getCMSJWT();

        if (!jwt) {
          console.error('No JWT token found for pet deletion');
          return;
        }

        let petId = null;

        // Find pet ID in pet object
        if (petToDelete.id) {
          petId = petToDelete.id;
        }
        
        // If not found in pet object, check cache
        if (!petId) {
          const { getCMSPets } = await import('./services/auth.service');
          const cachedPets = getCMSPets();
          const cachedPet = cachedPets.find((cachedPet: any) => 
            cachedPet.name === petToDelete.name && 
            cachedPet.type === petToDelete.type
          );
          if (cachedPet && cachedPet.id) {
            petId = cachedPet.id;
          }
        }

        if (!petId) {
          console.error('❌ Pet ID not found. Cannot delete pet.');
          console.error('Pet details:', petToDelete);
          alert('Unable to delete pet. Pet ID not found. Please refresh the page and try again.');
          return;
        }

        console.log('🚀 Calling DELETE API for pet ID:', petId);

        // Call DELETE API
        const response = await fetch(`${CMS_BASE_URL}/api/pet?id=${petId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwt}`
          }
        });

        if (response.ok) {
          // Update cache - remove pet from cms_pets
          updatePetsCache({ id: petId }, 'delete');
          
          // Show success notification
          Notification.success({
            title: 'Pet Deleted',
            content: `${petToDelete.name} has been deleted successfully.`,
            duration: 3000
          });
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to delete pet: ${response.status} - ${errorText}`);
        }
      } else {
        // Public user - delete from public pets storage
        const { getPublicPets, setPublicPets } = await import('./services/auth.service');
        const publicPets = getPublicPets();
        const updatedPublicPets = publicPets.filter((_, i) => i !== index);
        setPublicPets(updatedPublicPets);
        
        // Show success notification
        Notification.success({
          title: 'Pet Deleted',
          content: `${petToDelete.name} has been deleted successfully.`,
          duration: 3000
        });
      }
      
      // Update local state immediately for responsiveness
      setPets(prevPets => {
        const newPets = prevPets.filter((_, i) => i !== index);
        
        // If all pets are removed, reset selected pets and go back to main
        if (newPets.length === 0) {
          setSelectedPets([]);
          setCurrentStep('main');
        }
        return newPets;
      });
      
      // Note: PetProfileSuccess now handles its own refresh after deletion

    } catch (error) {
      console.error('💥 Error deleting pet:', error);
      alert('An error occurred while deleting the pet. Please try again.');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePetUpdate = (index: number, updatedPet: Pet) => {
    // Update the selected pets array
    setSelectedPets(prevSelectedPets => 
      prevSelectedPets.map((pet, i) => i === index ? updatedPet : pet)
    );
    // Also update the main pets array if the pet exists there
    setPets(prevPets => {
      const petIndex = prevPets.findIndex(p => p.name === selectedPets[index]?.name);
      if (petIndex !== -1) {
        return prevPets.map((pet, i) => i === petIndex ? updatedPet : pet);
      }
      return prevPets;
    });
  };



  const handleReady = () => {
    setCurrentStep('select-pets');
  };

  const handleLoginError = (error: any) => {
    console.error('Login error:', error);
    
    // Show user-friendly notification for login failures
    Notification.error({
      title: 'Login Failed',
      content: 'Unable to sign in. Please check your connection and try again.',
      duration: 5000
    });
  };

  // Demo mode is only active when on the demo store URL or when the demo store is selected
  const currentSlug = window.location.pathname.split('/')[1];
  const isOnDemoUrl = currentSlug === DEMO_STORE_SLUG;
  const demoMode = isOnDemoUrl || (isDemoStore(selectedStore) && isDemoModeActive());

  // Show 404 page for unknown routes
  if (showNotFound) {
    return (
      <DemoModeProvider isDemoMode={demoMode}>
        <Lazy>
          <NotFound
            onGoHome={() => {
              setShowNotFound(false);
              if (demoMode) {
                navigate(`/${DEMO_STORE_SLUG}`);
              }
            }}
            isDemoMode={demoMode}
          />
        </Lazy>
      </DemoModeProvider>
    );
  }

  return (
    <DemoModeProvider isDemoMode={demoMode}>
          <div className="app">
            {/* Demo mode banner */}
            {demoMode && (
              <div className="demo-banner">
                <span>You are viewing a demo store.</span>
                <a href="https://mypetgroomer.com.au" target="_blank" rel="noopener noreferrer">
                  Sign up to create your own
                </a>
              </div>
            )}
            {/* Only show location bar on main page */}
            {currentStep === 'main' && (
              <div className="location-bar">
                {selectedStore ? (
                  <span onClick={() => setLocationOverlayOpen(true)}>{selectedStore.name}</span>
                ) : (
                  <span onClick={() => setLocationOverlayOpen(true)}>Select location</span>
                )}
              </div>
            )}

            {currentStep === 'main' && (
              <MainPage
                onContinue={handleMainContinue}
                onMenuOpen={() => setIsMenuOpen(true)}
                onBooking={handleStartBooking}
                selectedStore={selectedStore}
                onLocationSelect={handleLocationSelect}
                storeCount={storeCount}
                companyLogoUrl={companyLogoUrl}
          user={user}
          onLogin={async (profile) => {
            setUser(profile);
            setIsAuthenticated(true);
            window.dispatchEvent(new Event(SIGNED_IN_EVENT));
            const { storageService } = await import('./services/storage.service');
            storageService.setItem('user', JSON.stringify(profile));
          }}
          onLogout={handleLogout}
          onLoginError={handleLoginError}
          onLoginClick={() => setShowLoginGate(true)}
              />
            )}
            {currentStep === 'pet-profile-success' && (
                <Lazy><PetProfileSuccess
                  pets={isLoadingPets ? [] : pets}
                  onBack={() => setCurrentStep('main')}
                  onEdit={(index: number) => { setEditingPetIndex(index); setCurrentStep('edit-pet-profile'); }}
                  onDelete={handlePetDeletion}
                  onAdd={() => navigate('/pet/create')}
                  onReady={handleReady}
                  isLoadedFromCMS={shouldShowExistingPets}
                  isParentLoading={isLoadingPets}
                  onPetsLoaded={handlePetsLoaded}
                  // onRefreshReady={handleRefreshReady} // REMOVED - using self-sufficient approach
                /></Lazy>
            )}
            {currentStep === 'edit-pet-profile' && typeof editingPetIndex === 'number' && pets[editingPetIndex] && (
              <Lazy><PetSheet
                pet={pets[editingPetIndex]}
                signedIn={isAuthenticated}
                onSaved={updatedPet => {
                  setPets(pets => pets.map((pet, i) => (i === editingPetIndex ? updatedPet : pet)));
                  setCurrentStep('pet-profile-success');
                  setEditingPetIndex(null);
                }}
                onDeleted={() => {
                  setPets(petsStore.all());
                  setCurrentStep('pet-profile-success');
                  setEditingPetIndex(null);
                }}
                onClose={() => { setCurrentStep('pet-profile-success'); setEditingPetIndex(null); }}
              /></Lazy>
            )}
            {currentStep === 'select-pets' && (
              <Lazy><SelectPets
                pets={pets}
                onBack={handleBack}
                onNext={handlePetSelection}
              /></Lazy>
            )}
            {(currentStep === 'booking' || currentStep === 'service') && (
              // The old services page retired with phase 3c (G17): what led to it
              // now opens the services step, signed in or not.
              <Navigate to={getBookingUrl('services', selectedStore)} replace />
            )}
            {isMenuOpen && (
              <MenuOverlay
                onClose={() => setIsMenuOpen(false)}
                onBooking={handleStartBooking}
                user={user}
                onLogin={handleLogin}
                onLogout={handleLogout}
                onLoginError={handleLoginError}
                onLoginClick={() => setShowLoginGate(true)}
              />
            )}

            {!isAuthenticated && (
              <GoogleOneTap
                onLoginSuccess={() => {
                  setIsAuthenticated(true);
                  const userData = localStorage.getItem('cms_user');
                  if (userData) setUser(JSON.parse(userData));
                }}
              />
            )}

            <LoginGate
              isOpen={showLoginGate}
              onClose={() => setShowLoginGate(false)}
              onLoginSuccess={() => {
                setIsAuthenticated(true);
                const userData = localStorage.getItem('cms_user');
                if (userData) setUser(JSON.parse(userData));
                // A promotion claim may be waiting on this sign-in.
                window.dispatchEvent(new Event(SIGNED_IN_EVENT));
              }}
            />


            
            {/* LocationOverlay for store selection */}
            <LocationOverlay
              isOpen={locationOverlayOpen}
              onClose={() => setLocationOverlayOpen(false)}
              onLocationSelect={handleLocationSelect}
              isDemoMode={demoMode}
              demoStoreId={selectedStore?.id}
            />
          </div>
    </DemoModeProvider>
  );
}

/**
 * A location that isn't taking online bookings takes none here either: the
 * plan has ended, the location is parked, or payouts aren't set up
 * (mpgadmin D17). The home page says so and offers a call; a bookmarked
 * /book link or a cached page lands here instead, and goes home to read it.
 * The server refuses the booking regardless — this just says it earlier and
 * kinder.
 */
const BookingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { storeSlug } = useParams();
  return storeService.canBookOnline(bookingStore(storeSlug)) ? <>{children}</> : <Navigate to="/" replace />;
};

/** The location being booked: the one in the link, else the one chosen. */
const bookingStore = (storeSlug?: string): Store | null => {
  if (storeSlug) {
    const fromLink = findStoreBySlug(storeService.getCachedStores(), storeSlug);
    if (fromLink) return fromLink;
  }
  try {
    const stored = localStorage.getItem('selectedStore');
    return stored ? (JSON.parse(stored) as Store) : null;
  } catch {
    return null;
  }
};

function App() {
  return (
      <BrowserRouter>
        {/* Global announcement banner (managed in Strapi) — shown on every storefront/route */}
        <AnnouncementBanner />
        {/* A salon's promotion pop-up, on the home page only (SF 02). */}
        <PromotionPopup />
        {/* Default SEO: noIndex for all pages; MainPage & the services step override via Helmet's last-wins */}
        <SEO noIndex />
        {/* Staging noindex is enforced INSIDE the SEO component now — a root-level
            <SEO noIndex /> is last-wins-defeated by any page that renders its own. */}
        <Routes>
          {/* Lazy-loaded routes wrapped with Suspense + ErrorBoundary */}
          <Route path="/spc" element={<Lazy><StripeRedirect /></Lazy>} />
          <Route path="/verify-email" element={<Lazy><VerifyEmail /></Lazy>} />
          <Route path="/reset-password" element={<Lazy><ResetPassword /></Lazy>} />
          {/* Back from Stripe: paid, or not (Stripe's Back link is /payment/cancel). */}
          <Route path="/payment/success" element={<Lazy><BookedPage /></Lazy>} />
          <Route path="/payment/cancel" element={<Lazy><BookedPage /></Lazy>} />
          <Route path="/payment/failure" element={<Lazy><BookedPage /></Lazy>} />
          <Route path="/profile/edit" element={<Lazy><EditUserProfile /></Lazy>} />
          <Route path="/profile/pets" element={<Lazy><PetProfiles /></Lazy>} />
          <Route path="/profile/addresses" element={<Lazy><AddressesPage /></Lazy>} />
          <Route path="/t/:token" element={<Lazy><GapOfferPage /></Lazy>} />
          <Route path="/profile/packages" element={<MyPackagesWrapper />} />
          {/* MPG Perks — the customer's wallet. Authenticated, noindex. */}
          <Route path="/my-perks" element={<PerksWrapper page="landing" />} />
          <Route path="/my-perks/history" element={<PerksWrapper page="history" />} />
          <Route path="/my-perks/member/:perkId" element={<PerksWrapper page="member" />} />
          <Route path="/my-perks/:entitlementId" element={<PerksWrapper page="detail" />} />
          <Route path="/my-perks/:entitlementId/use" element={<PerksWrapper page="use" />} />
          {/* Salon promotions: the client's own and the business's offers (SF 05). */}
          <Route path="/promotions" element={<PromotionsWrapper />} />
          {/* Waitlist — the customer's own requests, with cancel. Authenticated. */}
          <Route path="/my-waitlist" element={<WaitlistWrapper />} />
          <Route path="/pet/create" element={<Lazy><FirstPetFlow /></Lazy>} />
          <Route path="/appointments" element={<AppointmentsWrapper />} />
          <Route path="/appointments/:appointmentId" element={<Lazy><MyBookingPage /></Lazy>} />
          <Route path="/reschedule/:appointmentId" element={<Lazy><ReschedulePage /></Lazy>} />
          <Route path="/book" element={<Navigate to="/book/services" replace />} />
          <Route path="/book/summary" element={<BookingGate><Lazy><PaymentTransitionWrapper /></Lazy></BookingGate>} />
          <Route path="/book/:step/:storeSlug" element={<BookingGate><BookingFlowWrapper /></BookingGate>} />
          <Route path="/book/:step" element={<BookingGate><BookingFlowWrapper /></BookingGate>} />
          <Route path="/:bookingId/success" element={<Lazy><BookedPage /></Lazy>} />
          {/* Runtime sitemap - uses window.location.origin for multi-tenant support */}
          <Route path="/sitemap.xml" element={<Lazy><SitemapRoute /></Lazy>} />
          {/* Main app content - not lazy loaded (critical path) */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </BrowserRouter>
  );
}

export default App;
